import { Box, Typography } from '@mui/material';
import React from 'react';
import errorImageSmall from '../assets/errorImageSmall.svg';
import { useCommonWebTranslation } from '../hooks';
import { getErrorMessage } from '../utils';
/**
 * Displays a centered partial error message component with optional title, message,
 * and child elements. Used to inform the user about recoverable errors within a portion
 * of the page or layout.
 *
 * @param {PartialErrorProps} props - Component props.
 * @param {string} [props.title] - Custom title text to display.
 * @param {string} [props.message] - Custom message text to display.
 * @param {unknown} [props.error] - Optional error object to extract the message from.
 * @param {React.ReactNode} [props.children] - Optional additional content below the message.
 * @returns {JSX.Element} The rendered partial error UI.
 */
export const PartialError = (props) => {
    const { t } = useCommonWebTranslation();
    const defaultTitle = t('partialError.title');
    let defaultMessage = undefined;
    if (props.error) {
        defaultMessage = getErrorMessage(props.error);
        if (!defaultMessage.endsWith('.') && !defaultMessage.endsWith('?') && !defaultMessage.endsWith('!')) {
            defaultMessage += '.';
        }
    }
    return (React.createElement(Box, { sx: {
            boxSizing: 'border-box',
            width: '100%',
            height: '100%',
            py: 2,
            overflowX: 'hidden',
            overflowY: 'auto',
            display: 'flex',
        }, "data-test": 'partial-error' },
        React.createElement(Box, { sx: { margin: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' } },
            React.createElement("img", { src: errorImageSmall, alt: '', style: { display: 'block', width: 'calc(min(400px, 100%)' } }),
            React.createElement(Typography, { variant: 'h6', sx: { mt: 3, mb: 1, textAlign: 'center' }, "data-test": 'error-title' }, props.title ? props.title : defaultTitle),
            (props.message || defaultMessage) && (React.createElement(Typography, { variant: 'body1', sx: { mb: 3, textAlign: 'center' }, "data-test": 'error-message' }, props.message ? props.message : defaultMessage)),
            props.children && React.createElement(Box, null, props.children))));
};
